import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CircularProgress, Divider, Icon, Modal } from "@mui/material";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import esLocale from "@fullcalendar/core/locales/es";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "navbars/DashboardNavbar";

import { useNotification } from "../../context/NotificationContext";
import dateService from "../../services/dateService";
import taskService from "../../services/taskService";

const Calendar = () => {
  const { showNotification } = useNotification();
  const [dates, setDates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchEvents() {
      try {
        const [datesData, tasksData] = await Promise.all([
          dateService.getAllDates(),
          taskService.getAllTasks(),
        ]);
        if (Array.isArray(datesData)) setDates(datesData);
        if (Array.isArray(tasksData)) setTasks(tasksData);
      } catch {
        showNotification("Error al cargar los eventos", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchEvents();
  }, []);

  const [open, setOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedEvent(null);
  };

  const isPast = (dateStr) => {
    if (!dateStr) return false;
    return new Date(String(dateStr).replace(" ", "T")) <= new Date();
  };

  const calendarEvents = [
    ...dates.map((dt) => ({
      id: `date-${dt.id}`,
      title: dt.numero
        ? `${dt.numero}/${dt.anio} - ${dt.entity}`
        : `Exp. ${dt.expediente_id} - ${dt.entity}`,
      start: dt.arranged_date,
      display: "block",
      backgroundColor: "rgb(133, 173, 89)",
      borderColor: "rgb(133, 173, 89)",
      textColor: "white",
      extendedProps: {
        type: "date",
        expediente_id: dt.expediente_id,
        caratula: dt.caratula,
        part: dt.entity,
        contact: dt.contact,
        description: dt.description,
        link: dt.link,
        isPast: isPast(dt.arranged_date),
      },
    })),
    ...tasks
      .filter((t) => t.finish_date)
      .map((t) => ({
        id: `task-${t.id}`,
        title: t.caratula ? `${t.title} — ${t.caratula}` : t.title,
        start: t.finish_date,
        display: "block",
        backgroundColor: "#1A73E8",
        borderColor: "#1A73E8",
        textColor: "white",
        extendedProps: {
          type: "task",
          expediente_id: t.expediente_id,
          title: t.title,
          description: t.description,
          caratula: t.caratula,
          isPast: isPast(t.finish_date),
        },
      })),
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar title="Listado de eventos" />
      <Card>
        <CardContent>
          {loading ? (
            <MDBox display="flex" justifyContent="center" p={4}>
              <CircularProgress size={32} color="info" />
            </MDBox>
          ) : (
            <MDBox
              mt={1}
              fontSize="small"
              color="text"
              sx={{
                "& .fc-col-header-cell-cushion": {
                  textTransform: "uppercase",
                },
                "& .fc-col-header-cell": {
                  backgroundColor: (theme) => theme.palette.background.default,
                },
                "& .fc-list-day-cushion": {
                  backgroundColor: (theme) => theme.palette.background.default,
                },
                "& .fc-toolbar-title::first-letter": {
                  textTransform: "uppercase",
                },
                ".fc-list-event-hover-bg-color": {
                  backgroundColor: (theme) => theme.palette.background.default,
                  cursor: "pointer",
                },
                "& .fc-event-title": {
                  whiteSpace: "normal",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  lineHeight: 1.2,
                  fontWeight: "bold",
                },
                "& .fc-daygrid-event": {
                  alignItems: "flex-start",
                  padding: "3px 5px",
                },
                "& .fc-daygrid-event-dot": {
                  display: "none",
                },
                "& .fc-daygrid-event .fc-event-title": {
                  color: "white !important",
                },
                "& .fc-daygrid-block-event .fc-event-main-frame": {
                  flexDirection: "column",
                  gap: 0,
                },
                "& .fc-daygrid-block-event .fc-event-title-container": {
                  order: 1,
                },
                "& .fc-daygrid-block-event .fc-event-time": {
                  order: 2,
                  fontSize: "0.65rem",
                  lineHeight: 1,
                  fontWeight: "normal",
                  opacity: 0.85,
                  color: "white",
                  paddingTop: "1px",
                },
                "& .fc-daygrid-block-event .fc-event-time::after": {
                  content: '" hs"',
                },
              }}
            >
              <FullCalendar
                locale={esLocale}
                height="auto"
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                headerToolbar={{
                  left: "title",
                  center: "dayGridMonth,listMonth",
                  right: "today prev,next",
                }}
                initialView="dayGridMonth"
                dayMaxEvents={true}
                eventClick={handleEventClick}
                events={calendarEvents}
                eventDidMount={(info) => {
                  if (info.event.extendedProps.isPast) {
                    info.el.style.opacity = "0.45";
                  }
                }}
              />
            </MDBox>
          )}
        </CardContent>
      </Card>
      <Modal open={open} onClose={handleClose}>
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: (theme) => theme.palette.background.default,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            minWidth: 300,
          }}
        >
          {selectedEvent && (
            <>
              <MDBox px={2} textAlign="center" lineHeight={1.25}>
                <MDBox pb={2} mx={3} mt={-3} display="flex" justifyContent="center">
                  <MDBox
                    display="grid"
                    justifyContent="center"
                    alignItems="center"
                    bgColor={selectedEvent.extendedProps.isPast ? "secondary" : "info"}
                    color="white"
                    width="4rem"
                    height="4rem"
                    shadow="md"
                    borderRadius="lg"
                    variant="gradient"
                  >
                    <Icon fontSize="default">
                      {selectedEvent.extendedProps.type === "task" ? "task_alt" : "schedule"}
                    </Icon>
                  </MDBox>
                </MDBox>

                {selectedEvent.extendedProps.type === "task" ? (
                  <>
                    <MDTypography variant="h6" fontWeight="medium">
                      {selectedEvent.extendedProps.title}
                    </MDTypography>
                    {selectedEvent.extendedProps.caratula && (
                      <>
                        <MDTypography pt={1} display="block" variant="button" fontWeight="medium">
                          EXPEDIENTE
                        </MDTypography>
                        <MDTypography variant="caption" fontWeight="regular" color="text" style={{ whiteSpace: "pre-line" }}>
                          {selectedEvent.extendedProps.caratula}
                        </MDTypography>
                      </>
                    )}
                    <MDTypography pt={1} display="block" variant="button" fontWeight="medium">
                      VENCIMIENTO
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="regular" color="text">
                      {selectedEvent.start?.toLocaleDateString()}
                    </MDTypography>
                    {selectedEvent.extendedProps.description && (
                      <>
                        <MDTypography pt={1} display="block" variant="button" fontWeight="medium">
                          DESCRIPCIÓN
                        </MDTypography>
                        <MDTypography variant="caption" fontWeight="regular" color="text" style={{ whiteSpace: "pre-line" }}>
                          {selectedEvent.extendedProps.description}
                        </MDTypography>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                      {selectedEvent.start?.toLocaleDateString()}
                    </MDTypography>
                    {selectedEvent.extendedProps.caratula && (
                      <>
                        <MDTypography pt={1} display="block" variant="button" fontWeight="medium">
                          EXPEDIENTE
                        </MDTypography>
                        <MDTypography variant="caption" fontWeight="regular" color="text" style={{ whiteSpace: "pre-line" }}>
                          {selectedEvent.extendedProps.caratula}
                        </MDTypography>
                      </>
                    )}
                    <MDTypography pt={1} display="block" variant="button" fontWeight="medium">
                      LUGAR DE REUNIÓN
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="regular" color="text" style={{ whiteSpace: "pre-line" }}>
                      {selectedEvent.extendedProps.link ? selectedEvent.extendedProps.link : " - "}
                    </MDTypography>
                    <MDTypography pt={1} display="block" variant="button" fontWeight="medium">
                      PARTE REPRESENTADA
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="regular" color="text" style={{ whiteSpace: "pre-line" }}>
                      {selectedEvent.extendedProps.part ? selectedEvent.extendedProps.part : " - "}
                    </MDTypography>
                    <MDTypography pt={1} display="block" variant="button" fontWeight="medium">
                      CONTACTO
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="regular" color="text" style={{ whiteSpace: "pre-line" }}>
                      {selectedEvent.extendedProps.contact}
                    </MDTypography>
                    <MDTypography pt={1} display="block" variant="button" fontWeight="medium">
                      DESCRIPCIÓN
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="regular" color="text" style={{ whiteSpace: "pre-line" }}>
                      {selectedEvent.extendedProps.description}
                    </MDTypography>
                  </>
                )}

                <Divider />
                {selectedEvent.extendedProps.expediente_id && (
                  <MDButton
                    color="info"
                    onClick={() => navigate("/file/detail/" + selectedEvent.extendedProps.expediente_id)}
                  >
                    Ver expediente
                  </MDButton>
                )}
                <MDBox pt={1}>
                  <MDButton variant="text" color="secondary" onClick={handleClose}>
                    Cerrar
                  </MDButton>
                </MDBox>
              </MDBox>
            </>
          )}
        </MDBox>
      </Modal>
    </DashboardLayout>
  );
};

export default Calendar;
